import { LayerExtentUserPreferences } from 'components/maps/LayersToolbar/LayerExtentPreferences';
import { useGetDefaultMapConfigQuery } from '@kinetic-ui/data-layer';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LngLatBoundsLike, LngLatLike } from 'maplibre-gl';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';
import { ViewState } from 'react-map-gl/dist/esm/types';
import { isBoundsMapConfig, isViewStateMapConfig } from 'kineticUtils/maps';
import { useUserTenantPreference } from '../preferences';
import { FlyToInterpolator } from 'deck.gl';

export const US_CENTER: LngLatLike = [39.26648466186085, -101.42562726650448];
export const DEFAULT_ZOOM = 14;
export const US_BOUNDS: LngLatBoundsLike = [
  [-126.65039062499999, 22.187404991398775],
  [-67.67578124999999, 47.45780853075031],
];

export const useMapLocation = () => {
  // Get Default Map Config
  const configResult = useGetDefaultMapConfigQuery();

  const { value: layoutPreferences } = useUserTenantPreference<LayerExtentUserPreferences>({
    key: PREFERENCE_KEYS.EXTENT_LAYERS,
  });

  // Get User Map Preference
  const defaultKey = layoutPreferences
    ? Object.keys(layoutPreferences).find((key) => {
        return layoutPreferences[key].default;
      }) ?? null
    : null;
  const mapConfigViewState = useMemo(() => {
    const config = configResult.data?.mapConfig;
    if (isViewStateMapConfig(config)) {
      const { geometry, zoom, layers } = config;
      return {
        layers,
        longitude: geometry.coordinates[0],
        latitude: geometry.coordinates[1],
        zoom: zoom,
      };
    } else {
      return null;
    }
  }, [configResult.data?.mapConfig]);

  // set Default Bounds
  const lineString = useMemo(() => {
    const config = configResult.data?.mapConfig;
    if (isBoundsMapConfig(config)) {
      return config.geometry?.coordinates;
    } else {
      return null;
    }
  }, [configResult.data?.mapConfig]);

  const latLngBounds: LngLatBoundsLike | undefined = useMemo(
    () =>
      lineString
        ? [
            [lineString[0][0], lineString[0][1]],
            [lineString[1][0], lineString[1][1]],
          ]
        : undefined,
    [lineString]
  );

  const defaultLineString: LngLatBoundsLike | undefined = defaultKey
    ? (layoutPreferences[defaultKey].bounds as LngLatBoundsLike)
    : latLngBounds;

  const defaultBounds = useMemo(() => {
    return defaultLineString ?? US_BOUNDS;
  }, [defaultLineString]);

  // Set initial map state
  const [bounds, setBounds] = useState<LngLatBoundsLike | undefined>(defaultBounds);
  const [viewState, setViewState] = useState<Partial<ViewState>>();
  const [layers, setLayers] = useState<string[]>([]);

  const handleSetViewState = useCallback((newViewState) => {
    setViewState({
      ...newViewState,
      transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
      transitionDuration: 'auto',
    });
  }, []);

  useEffect(() => {
    if (defaultKey) {
      handleSetViewState({ ...layoutPreferences[defaultKey].viewState });
      setLayers(layoutPreferences[defaultKey].layers ?? []);
    } else if (mapConfigViewState) {
      handleSetViewState(mapConfigViewState);
      setLayers(mapConfigViewState.layers);
    } else if (latLngBounds) {
      setBounds(latLngBounds);
    }
  }, [defaultKey, latLngBounds, layoutPreferences, mapConfigViewState, handleSetViewState]);
  return {
    bounds,
    setBounds,
    viewState,
    layers,
    setLayers,
  };
};
